/**
 * InfographicModal.js
 *
 * Accepts a PDF file, an optional focus hint, and a detail level,
 * submits them to POST /infographic/generate, then returns to chat where
 * progress and the generated image are displayed.
 *
 * Styling re-uses protest-modal / deep-research-modal classes.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Icons } from './Icons';

const ACCEPTED_PDF = '.pdf,application/pdf';

function InfographicModal({ isOpen, onClose, onSubmit }) {
  const [pdfFile, setPdfFile] = useState(null);
  const [focus, setFocus] = useState('');
  const [detail, setDetail] = useState('standard');
  const [error, setError] = useState('');

  const pdfInputRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (!isOpen) return;
    setPdfFile(null);
    setFocus('');
    setDetail('standard');
    setError('');
    if (pdfInputRef.current) pdfInputRef.current.value = '';
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!pdfFile) {
      setError('Please select a PDF file.');
      return;
    }
    setError('');
    onSubmit({ pdfFile, focus: focus.trim(), detail });
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="protest-modal-overlay" onClick={handleOverlayClick}>
      <div
        className="protest-modal deep-research-modal"
        style={{ maxWidth: 540, width: '95%' }}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="protest-modal-header">
          <div className="protest-modal-title">
            <Icons.FileText />
            <h2>Infographic Generator</h2>
          </div>
          <button
            type="button"
            className="protest-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <Icons.Close />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <form onSubmit={handleSubmit} className="protest-modal-content">
            {/* PDF file picker */}
            <div className="protest-location-section">
              <h3>PDF Document</h3>
              <div className="protest-location-field">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <input
                    ref={pdfInputRef}
                    type="file"
                    accept={ACCEPTED_PDF}
                    style={{ display: 'none' }}
                    onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                  />
                  <button
                    type="button"
                    className="protest-btn-cancel"
                    style={{ minWidth: 110 }}
                    onClick={() => pdfInputRef.current?.click()}
                  >
                    {pdfFile ? 'Change PDF…' : 'Choose PDF…'}
                  </button>
                  {pdfFile && (
                    <span style={{ fontSize: '0.88em', color: '#444', wordBreak: 'break-all' }}>
                      {pdfFile.name}
                      <button
                        type="button"
                        aria-label="Remove file"
                        style={{
                          marginLeft: 6,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#888',
                          fontSize: '1em',
                          verticalAlign: 'middle',
                        }}
                        onClick={() => {
                          setPdfFile(null);
                          if (pdfInputRef.current) pdfInputRef.current.value = '';
                        }}
                      >
                        ×
                      </button>
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Focus hint (optional) */}
            <div className="protest-location-section">
              <h3>Focus <span style={{ fontWeight: 400, fontSize: '0.85em', color: '#6b7280' }}>(optional)</span></h3>
              <p className="deep-research-modal-desc" style={{ marginTop: 0 }}>
                Describe a specific section or topic to focus on. Leave empty to cover the entire document.
              </p>
              <div className="protest-location-field">
                <input
                  type="text"
                  className="protest-location-input"
                  placeholder="e.g. financial results, executive summary, risk section…"
                  value={focus}
                  onChange={(e) => setFocus(e.target.value)}
                  maxLength={200}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            {/* Detail level */}
            <div className="protest-location-section">
              <h3>Detail Level</h3>
              <div style={{ display: 'flex', gap: 12 }}>
                {['standard', 'detailed'].map((level) => (
                  <label
                    key={level}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer',
                      fontWeight: detail === level ? '600' : '400',
                      color: detail === level ? '#1A56DB' : '#374151',
                    }}
                  >
                    <input
                      type="radio"
                      name="detail"
                      value={level}
                      checked={detail === level}
                      onChange={() => setDetail(level)}
                    />
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </label>
                ))}
              </div>
            </div>

            {error && <div className="protest-error">{error}</div>}

            <div className="protest-modal-actions">
              <button
                type="button"
                className="protest-btn-cancel"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="protest-btn-submit"
                disabled={!pdfFile}
              >
                <Icons.Search />
                Generate Infographic
              </button>
            </div>
          </form>
      </div>
    </div>
  );
}

export default InfographicModal;
